import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  IconButton,
  Text,
  Collapse,
  Portal,
  useDisclosure,
  Flex,
  Badge,
  Avatar,
  Icon,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FiMenu,
  FiX,
  FiHome,
  FiUsers,
  FiUser,
  FiCalendar,
  FiDollarSign,
  FiSettings,
  FiLogOut,
  FiShield,
  FiUserPlus,
  FiChevronRight,
  FiGrid,
  FiExternalLink,
  FiMail,
  FiDatabase,
  FiVideo,
  FiBarChart2,
  FiGift,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginWithSmsModal } from '../pages/authentication';
import { getColor } from '../brandConfig';
import { useShiftKey, isDevMode } from '../hooks/useShiftKey';
import { renderPermissionTooltip } from './devPermissionTooltip';
import { computeMenuPlacement } from '../hooks/useDraggableFloat';
import { useModulePermissions } from '../contexts/ModulePermissionsContext';

// Import module configs
import profileModuleConfig from '../pages/profile/moduleConfig';
import governanceModuleConfig from '../pages/governance/moduleConfig';
import subscriptionModuleConfig from '../pages/subscriptions/moduleConfig';
import clientsModuleConfig from '../pages/clients/moduleConfig';
import companiesModuleConfig from '../pages/companies/moduleConfig';
import { calendarsModuleConfig } from '../pages/calendars/moduleConfig';
import billsModuleConfig from '../pages/bills/moduleConfig';
import emailsModuleConfig from '../pages/emails/moduleConfig';
import adminModuleConfig from '../pages/admin/moduleConfig';
import opportunitiesModuleConfig from '../pages/opportunities/moduleConfig';
import companyKnowledgeModuleConfig from '../pages/companyknowledge/moduleConfig';
import seoMetadataModuleConfig from '../pages/seometadata/moduleConfig';
import mediaCdnModuleConfig from '../pages/media-cdn/moduleConfig';
import analyticsModuleConfig from '../pages/analytics/moduleConfig';
import rewardsModuleConfig from '../pages/rewards/moduleConfig';
import { useNavbarState } from '../hooks/useNavbarState';

// Animations
const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

// Icon mapping for modules
const moduleIcons: Record<string, any> = {
  profile: FiUser,
  clients: FiUsers,
  companies: FiDatabase,
  calendars: FiCalendar,
  bills: FiDollarSign,
  opportunities: FiDollarSign,
  emails: FiMail,
  companyknowledge: FiDatabase,
  'media-cdn': FiVideo,
  analytics: FiBarChart2,
  rewards: FiGift,
  subscriptions: FiDollarSign,
  admin: FiSettings,
  seometadata: FiGrid,
};

export const FloatingNavbar: React.FC = () => {
  // Use persistent state management with client-specific storage
  const { isAuthenticated, user, logout, refreshAuth } = useAuth();
  const { colorMode } = useColorMode();
  // Use client ID for user-specific navbar state
  const clientId = user?.clientId || user?.client?.id || user?.id;


  const {
    isExpanded,
    setIsExpanded,
    activeCategory,
    setActiveCategory,
    expandedModules,
    toggleModuleExpanded,
    resetState,
  } = useNavbarState(clientId);


  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();

  // Dev-mode Shift+hover permission reveal — see vault/how-to/dev-mode-permission-hover.md
  const shiftHeld = useShiftKey();
  const devReveal = isDevMode() && shiftHeld;

  // Backend-resolved permission lookup for modules using the single-source-of-truth
  // pattern (empty `view: []` in the local moduleConfig — real perms live in the
  // backend `modulePermissions` query and get hydrated on login).
  const { getPermissions } = useModulePermissions();

  // Universal admin bypass — ADMIN and TENANT_MASTER_ADMIN see every module.
  // Matches the documented pattern: "ADMIN and TENANT_MASTER_ADMIN bypass all
  // checks in FloatingNavbar".
  const isAdminBypass = !!user?.permissions?.some((p: string) =>
    p === 'ADMIN' || p === 'TENANT_MASTER_ADMIN' || p === 'MASTER_ADMIN'
  );

  // Draggable position — per-client localStorage so each user gets their own saved position.
  // See vault/how-to/draggable-floating-launchers.md.
  const BUTTON_SIZE = 56;
  const EDGE_PAD = 8;
  const posStorageKey = `block52_floating_navbar_pos_${clientId || 'guest'}`;
  const getDefaultPos = () => {
    if (typeof window === 'undefined') return { left: 20, top: 600 };
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      return {
        left: window.innerWidth - BUTTON_SIZE - 16,
        top: window.innerHeight - BUTTON_SIZE - 96,
      };
    }
    return { left: 20, top: window.innerHeight - BUTTON_SIZE - 20 };
  };
  const [position, setPosition] = useState<{ left: number; top: number }>(() => {
    try {
      const saved = typeof window !== 'undefined' && localStorage.getItem(posStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') return parsed;
      }
    } catch { /* noop */ }
    return getDefaultPos();
  });

  // Re-clamp when viewport resizes (rotation, window resize) so the button never lands off-screen.
  useEffect(() => {
    const clamp = () => {
      setPosition((p) => ({
        left: Math.max(EDGE_PAD, Math.min(window.innerWidth - BUTTON_SIZE - EDGE_PAD, p.left)),
        top: Math.max(EDGE_PAD, Math.min(window.innerHeight - BUTTON_SIZE - EDGE_PAD, p.top)),
      }));
    };
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, []);

  // Drag state kept in refs so we don't re-render every pointermove.
  const dragRef = useRef<{ startX: number; startY: number; startLeft: number; startTop: number; moved: boolean } | null>(null);
  const justDraggedRef = useRef(false);

  const handleDragStart = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: position.left,
      startTop: position.top,
      moved: false,
    };
  };
  const handleDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) > 5) d.moved = true;
    if (!d.moved) return;
    setPosition({
      left: Math.max(EDGE_PAD, Math.min(window.innerWidth - BUTTON_SIZE - EDGE_PAD, d.startLeft + dx)),
      top: Math.max(EDGE_PAD, Math.min(window.innerHeight - BUTTON_SIZE - EDGE_PAD, d.startTop + dy)),
    });
  };
  const handleDragEnd = (_e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.moved) {
      try { localStorage.setItem(posStorageKey, JSON.stringify(position)); } catch { /* noop */ }
      justDraggedRef.current = true;
    }
    dragRef.current = null;
  };

  // Refresh auth data when component mounts and user is authenticated but data is missing
  useEffect(() => {

    if (isAuthenticated && (!user || !user.permissions)) {
      refreshAuth();
    }
  }, [isAuthenticated, user, refreshAuth]);

  // All available module configs
  const allModuleConfigs = [
    profileModuleConfig,
    clientsModuleConfig,
    companiesModuleConfig,
    calendarsModuleConfig,
    billsModuleConfig,
    opportunitiesModuleConfig,
    emailsModuleConfig,
    subscriptionModuleConfig,
    companyKnowledgeModuleConfig,
    mediaCdnModuleConfig,
    analyticsModuleConfig,
    rewardsModuleConfig,
    governanceModuleConfig,
    adminModuleConfig,
    seoMetadataModuleConfig,
  ];

  // Filter modules based on user permissions.
  // Three rules, in order:
  //   1. ADMIN / TENANT_MASTER_ADMIN / MASTER_ADMIN see every module (universal bypass).
  //   2. If the moduleConfig has a non-empty `view: [...]` array, treat that as the source of truth.
  //   3. Otherwise (single-source-of-truth modules with `view: []`), fall back to
  //      `useModulePermissions().getPermissions(moduleId, 'view')` — the backend-resolved list.
  const getAvailableModules = () => {
    if (!user || !user.permissions || user.permissions.length === 0) {
      return [];
    }

    if (isAdminBypass) {
      return allModuleConfigs;
    }

    return allModuleConfigs.filter(module => {
      const localView = module.permissions?.view ?? [];
      const effectiveView = localView.length > 0
        ? localView
        : getPermissions(module.id, 'view');

      // Empty effective view = public/unset — show by default.
      if (effectiveView.length === 0) return true;

      return user?.permissions?.some((permission: string) =>
        effectiveView.includes(permission)
      ) || false;
    });
  };

  // Group modules by category — kept tight + scannable.
  // Order top→bottom = day-to-day frequency: Sales first (revenue),
  // People/Sites next (ops), then Comms, Content, Marketplace, AI tools,
  // Insights, and Settings tucked at the bottom.
  const categorizedModules = {
    'Sales & CRM': ['clients', 'opportunities', 'bills'],

    'People & Resources': ['companies'],

    'Communication': ['calendars', 'emails'],

    'Content & Knowledge': ['companyknowledge', 'media-cdn'],

    'Marketplace & Rewards': ['rewards'],

    'Analytics & SEO': ['analytics', 'seometadata'],

    'Settings': ['profile', 'admin'],
  };

  const availableModules = isAuthenticated ? getAvailableModules() : [];


  const handleNavItemClick = (path: string) => {
    navigate(path);
    // Keep the menu state but close the expanded view
    setIsExpanded(false);
  };

  const handleOpenInNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsExpanded(false);
    // Reset navbar state on logout
    resetState();
  };

  // Filter navigation items based on user permissions
  const getVisibleNavItems = (navigation: any) => {
    if (!navigation) return [];

    let navItems: any[] = [];
    if (Array.isArray(navigation)) {
      navItems = navigation;
    } else if (navigation.sub && Array.isArray(navigation.sub)) {
      navItems = navigation.sub;
    }

    return navItems.filter((item: any) => {
      if (!item.permissions || item.permissions.length === 0) return true;

      const hasPermission = user?.permissions?.some((permission: string) =>
        item.permissions.includes(permission)
      ) || false;


      return hasPermission;
    });
  };

  // Close menu when clicking outside the FAB, the menu panel, or the
  // emergency-logout mini-FAB. Three Portals, three classNames to exempt.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isExpanded
          && !target.closest('.floating-navbar-container')
          && !target.closest('.floating-navbar-menu')
          && !target.closest('.floating-navbar-logout')) {
        setIsExpanded(false);
        setActiveCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, setIsExpanded, setActiveCategory]);

  // Viewport-safe placement for the expandable menu panel. Must render the
  // panel inside its own Portal with position: fixed so the computed top/left
  // are viewport-relative (NOT relative to the FAB's own 56px-wide container).
  // See vault/how-to/draggable-floating-launchers.md.
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const panelPlacement = computeMenuPlacement(
    { left: position.left, top: position.top, size: BUTTON_SIZE },
    { w: viewportW, h: viewportH },
    { preferredWidth: 400, estimatedHeight: 480, gap: 12 },
    EDGE_PAD,
  );

  return (
    <>
      {/* ===== Portal A — Menu panel =====
          Rendered in its OWN Portal at viewport coordinates so it's always
          positioned relative to the viewport, not the FAB's 56px-wide
          container. See vault/how-to/draggable-floating-launchers.md. */}
      <Portal>
        <Box
          className="floating-navbar-menu"
          position="fixed"
          {...(panelPlacement.top !== undefined ? { top: `${panelPlacement.top}px` } : {})}
          {...(panelPlacement.bottom !== undefined ? { bottom: `${panelPlacement.bottom}px` } : {})}
          left={`${panelPlacement.left}px`}
          width={`${panelPlacement.width}px`}
          zIndex={999}
          pointerEvents={isExpanded ? 'auto' : 'none'}
        >
          <Collapse in={isExpanded} animateOpacity>
            <Box
              bg={colorMode === 'light'
                ? "rgba(255, 255, 255, 0.98)"
                : "rgba(10, 10, 15, 0.95)"}
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              border="1px solid"
              borderColor={getColor("border.light", colorMode)}
              p={4}
              maxH="70vh"
              overflowY="auto"
              boxShadow={colorMode === 'light'
                ? "0 10px 40px rgba(0, 0, 0, 0.15)"
                : "0 20px 60px rgba(0, 0, 0, 0.8)"}
              animation={`${slideIn} 0.3s ease-out`}
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: getColor("secondary", colorMode),
                  borderRadius: '2px',
                },
              }}
            >

              {/* User Profile Section */}
              {isAuthenticated && user && (
                <Flex
                  align="center"
                  justify="space-between"
                  mb={4}
                  p={3}
                  bg={colorMode === 'light' ? "rgba(90, 200, 250, 0.05)" : "rgba(90, 200, 250, 0.1)"}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'light' ? "rgba(90, 200, 250, 0.1)" : "rgba(90, 200, 250, 0.2)"}
                >
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={`${user.fName || user.firstName || ""} ${user.lName || user.lastName || ""}`}
                      src={user.profilePhoto || undefined}
                      bg={getColor("secondary", colorMode)}
                    />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="600" color={colorMode === 'light' ? "gray.700" : "white"}>
                        {user.fName || user.firstName || 'User'} {user.lName || user.lastName || ''}
                      </Text>
                      <Text fontSize="xs" color={colorMode === 'light' ? "gray.500" : "gray.400"}>
                        {user.email}
                      </Text>
                    </VStack>
                  </HStack>
                  <IconButton
                    aria-label="Logout"
                    icon={<FiLogOut />}
                    size="sm"
                    variant="ghost"
                    color="red.400"
                    onClick={handleLogout}
                    _hover={{
                      bg: 'rgba(255, 0, 0, 0.1)',
                      transform: 'scale(1.1)',
                    }}
                  />
                </Flex>
              )}

              {/* Account links — Security for everyone, Onboarding for ONBOARDING permission. */}
              {isAuthenticated && user && (
                <VStack align="stretch" spacing={1} mb={4}>
                  <Button size="sm" variant="ghost" justifyContent="flex-start" leftIcon={<FiShield />} onClick={() => handleNavItemClick('/security')}>
                    Security &amp; sign-in
                  </Button>
                  {(user.permissions?.includes('ONBOARDING') || user.permissions?.includes('MASTER_ADMIN')) && (
                    <Button size="sm" variant="ghost" justifyContent="flex-start" leftIcon={<FiUserPlus />} onClick={() => handleNavItemClick('/onboarding')}>
                      Onboard people
                    </Button>
                  )}
                </VStack>
              )}

              {/* Quick Actions */}
              <HStack mb={4} spacing={2}>
                <Button
                  size="sm"
                  leftIcon={<FiHome />}
                  bg={getColor("secondary", colorMode)}
                  color="white"
                  fontWeight="600"
                  borderRadius="full"
                  _hover={{
                    bg: getColor("secondaryHover", colorMode),
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.2s"
                  onClick={() => {
                    navigate('/');
                    setIsExpanded(false);
                  }}
                >
                  Home
                </Button>
                {!isAuthenticated && (
                  <Button
                    size="sm"
                    bg={getColor("secondary", colorMode)}
                    color="white"
                    fontWeight="600"
                    borderRadius="full"
                    onClick={onLoginOpen}
                    _hover={{
                      bg: getColor("secondaryHover", colorMode),
                      transform: 'translateY(-2px)',
                    }}
                    transition="all 0.2s"
                  >
                    Sign In
                  </Button>
                )}
              </HStack>

              {/* Module Categories */}
              {isAuthenticated && (
                <VStack spacing={2} align="stretch">
                  {Object.entries(categorizedModules).map(([category, moduleIds]) => {
                    const categoryModules = availableModules.filter(m =>
                      moduleIds.includes(m.id)
                    );

                    if (categoryModules.length === 0) return null;

                    return (
                      <Box key={category}>
                        <Button
                          size="sm"
                          variant="ghost"
                          color={colorMode === 'light' ? "gray.600" : "gray.400"}
                          fontSize="xs"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          justifyContent="space-between"
                          w="full"
                          onClick={() => {
                            const newCategory = activeCategory === category ? null : category;
                            setActiveCategory(newCategory);
                          }}
                          _hover={{
                            bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                            color: getColor("secondary", colorMode),
                          }}
                          rightIcon={
                            <Box
                              transform={activeCategory === category ? 'rotate(90deg)' : 'rotate(0)'}
                              transition="transform 0.2s"
                            >
                              <FiChevronRight />
                            </Box>
                          }
                        >
                          {category}
                        </Button>

                        <Collapse in={activeCategory === category} animateOpacity>
                          <VStack spacing={1} align="stretch" mt={2} pl={4}>
                            {categoryModules.map((module) => {
                              const IconComponent = moduleIcons[module.id] || FiGrid;
                              const isModuleExpanded = expandedModules.has(module.id);
                              const visibleNavItems = getVisibleNavItems(module.navigation);

                              // Get the main module path (handle different navigation formats)
                              let mainModulePath = `/module/${module.id}`;
                              if (module.navigation) {
                                if (Array.isArray(module.navigation) && module.navigation[0]) {
                                  mainModulePath = module.navigation[0].path;
                                } else if (!Array.isArray(module.navigation) && typeof module.navigation === 'object' && 'main' in module.navigation) {
                                  mainModulePath = (module.navigation as any).main.path;
                                }
                              } else if ((module as any).routes?.[0]) {
                                mainModulePath = (module as any).routes[0].path;
                              }

                              // Permissions for the module-level button — use the first
                              // nav item's permissions since that's where the click lands.
                              const moduleButtonPerms: string[] =
                                (visibleNavItems[0]?.permissions as string[] | undefined) ?? [];
                              const moduleButtonLevel = visibleNavItems[0]?.permissionLevel as string | undefined;

                              return (
                                <Box key={module.id}>
                                  <HStack
                                    spacing={0}
                                    onMouseEnter={() => setHoveredItem(`module-${module.id}`)}
                                    onMouseLeave={() => setHoveredItem(null)}
                                  >
                                    <Tooltip
                                      label={
                                        devReveal
                                          ? renderPermissionTooltip({
                                              label: module.name,
                                              path: mainModulePath,
                                              moduleId: module.id,
                                              permissionLevel: moduleButtonLevel,
                                              resolved: moduleButtonPerms,
                                            })
                                          : module.name
                                      }
                                      placement="right"
                                      hasArrow
                                      isDisabled={!devReveal}
                                      bg={devReveal ? 'gray.800' : undefined}
                                      color={devReveal ? 'white' : undefined}
                                      px={devReveal ? 3 : undefined}
                                      py={devReveal ? 2 : undefined}
                                    >
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      color={colorMode === 'light' ? "gray.700" : "gray.300"}
                                      flex="1"
                                      justifyContent="flex-start"
                                      leftIcon={<Icon as={IconComponent} />}
                                      onClick={() => {
                                        // Navigate to the main module page
                                        handleNavItemClick(mainModulePath);
                                      }}
                                      position="relative"
                                      _hover={{
                                        bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                        color: getColor("accent", colorMode),
                                        transform: 'translateX(4px)',
                                        _before: {
                                          opacity: 1,
                                        }
                                      }}
                                      _before={{
                                        content: '""',
                                        position: 'absolute',
                                        left: '-16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '3px',
                                        height: '70%',
                                        bg: `linear-gradient(180deg, transparent, ${getColor("secondary", colorMode)}, transparent)`,
                                        borderRadius: 'full',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                      }}
                                      transition="all 0.2s"
                                    >
                                      {module.name}
                                    </Button>
                                    </Tooltip>

                                    {/* Open in new tab button - shows on hover */}
                                    <IconButton
                                      aria-label="Open in new tab"
                                      icon={<FiExternalLink size="12" />}
                                      size="xs"
                                      variant="ghost"
                                      color="gray.400"
                                      opacity={hoveredItem === `module-${module.id}` ? 1 : 0}
                                      pointerEvents={hoveredItem === `module-${module.id}` ? 'auto' : 'none'}
                                      onClick={() => handleOpenInNewTab(mainModulePath)}
                                      _hover={{
                                        bg: 'rgba(90, 200, 250, 0.2)',
                                        color: getColor("secondary", colorMode),
                                      }}
                                      transition="all 0.2s"
                                    />

                                    {/* Expand/collapse button */}
                                    {visibleNavItems.length > 0 && (
                                      <IconButton
                                        aria-label="Expand module"
                                        icon={
                                          <Box
                                            transform={isModuleExpanded ? 'rotate(90deg)' : 'rotate(0)'}
                                            transition="transform 0.2s"
                                          >
                                            <FiChevronRight size="14" />
                                          </Box>
                                        }
                                        size="xs"
                                        variant="ghost"
                                        color="gray.400"
                                        onClick={() => {
                                          toggleModuleExpanded(module.id);
                                        }}
                                        _hover={{
                                          bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                          color: getColor("secondary", colorMode),
                                        }}
                                      />
                                    )}
                                  </HStack>

                                  {/* Sub-navigation items */}
                                  <Collapse in={isModuleExpanded} animateOpacity>
                                    <VStack spacing={0.5} align="stretch" mt={1} pl={8}>
                                      {visibleNavItems.map((navItem: any) => (
                                        <HStack
                                          key={navItem.path}
                                          spacing={0}
                                          onMouseEnter={() => setHoveredItem(`nav-${navItem.path}`)}
                                          onMouseLeave={() => setHoveredItem(null)}
                                        >
                                          <Tooltip
                                            label={
                                              devReveal
                                                ? renderPermissionTooltip({
                                                    label: navItem.label,
                                                    path: navItem.path,
                                                    moduleId: module.id,
                                                    permissionLevel: navItem.permissionLevel,
                                                    resolved: (navItem.permissions as string[]) ?? [],
                                                  })
                                                : navItem.label
                                            }
                                            placement="right"
                                            hasArrow
                                            isDisabled={!devReveal}
                                            bg={devReveal ? 'gray.800' : undefined}
                                            color={devReveal ? 'white' : undefined}
                                            px={devReveal ? 3 : undefined}
                                            py={devReveal ? 2 : undefined}
                                          >
                                          <Button
                                            size="xs"
                                            variant="ghost"
                                            color={colorMode === 'light' ? "gray.600" : "gray.400"}
                                            flex="1"
                                            justifyContent="flex-start"
                                            leftIcon={navItem.icon ? <Text fontSize="xs">{navItem.icon}</Text> : undefined}
                                            onClick={() => handleNavItemClick(navItem.path)}
                                            fontSize="xs"
                                            fontWeight="400"
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.1)',
                                              color: getColor("secondary", colorMode),
                                              transform: 'translateX(2px)',
                                            }}
                                            transition="all 0.15s"
                                          >
                                            {navItem.label}
                                            {navItem.badge && (
                                              <Badge
                                                ml={1}
                                                colorScheme={navItem.badge.color || 'gray'}
                                                fontSize="2xs"
                                                variant="subtle"
                                              >
                                                {navItem.badge.text}
                                              </Badge>
                                            )}
                                          </Button>
                                          </Tooltip>

                                          {/* Open in new tab button for sub-items */}
                                          <IconButton
                                            aria-label="Open in new tab"
                                            icon={<FiExternalLink size="10" />}
                                            size="xs"
                                            variant="ghost"
                                            color="gray.500"
                                            opacity={hoveredItem === `nav-${navItem.path}` ? 1 : 0}
                                            pointerEvents={hoveredItem === `nav-${navItem.path}` ? 'auto' : 'none'}
                                            onClick={() => handleOpenInNewTab(navItem.path)}
                                            minW="20px"
                                            h="20px"
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.2)',
                                              color: getColor("secondary", colorMode),
                                            }}
                                            transition="all 0.2s"
                                          />
                                        </HStack>
                                      ))}
                                    </VStack>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </VStack>
                        </Collapse>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </Collapse>
        </Box>
      </Portal>

      {/* ===== Portal B — FAB =====
          Pinned at the user's dragged position. Kept separate from the menu
          Portal so menu placement is computed from viewport coordinates
          (not relative to the 56px FAB container). */}
      <Portal>
        <Box
          className="floating-navbar-container"
          position="fixed"
          top={`${position.top}px`}
          left={`${position.left}px`}
          zIndex={998}
        >
          <IconButton
            aria-label="Toggle navigation (drag to move)"
            icon={isExpanded ? <FiX /> : <FiMenu />}
            size="lg"
            borderRadius="full"
            bg={getColor("secondary", colorMode)}
            color="white"
            border="2px solid"
            borderColor="rgba(255, 255, 255, 0.2)"
            boxShadow="0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
            sx={{ touchAction: 'none' }}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
            onClick={() => {
              if (justDraggedRef.current) {
                justDraggedRef.current = false;
                return;
              }
              setIsExpanded(!isExpanded);
            }}
            _hover={{
              bg: getColor("secondaryHover", colorMode),
              transform: 'scale(1.1) rotate(180deg)',
              boxShadow: "0 6px 20px rgba(90, 200, 250, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
            }}
            _active={{
              transform: 'scale(0.95)',
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.2)"
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            animation={!isExpanded ? `${float} 3s ease-in-out infinite` : undefined}
            position="relative"
          />
        </Box>
      </Portal>

      {/* ===== Portal C — Emergency logout mini-FAB =====
          Always visible while authenticated. Escape hatch for broken auth state:
          if permissions never load, `user` is null, or the menu won't open for
          any reason, tapping this immediately clears the session. Sits directly
          above the main FAB and tracks its dragged position.
          See vault/how-to/draggable-floating-launchers.md → "Emergency logout mini-FAB". */}
      {isAuthenticated && (
        <Portal>
          <Box
            className="floating-navbar-logout"
            position="fixed"
            top={`${Math.max(EDGE_PAD, position.top - 40)}px`}
            left={`${position.left + 14}px`}
            zIndex={997}
            display={{ base: "none", md: "block" }}
          >
            <Tooltip
              hasArrow
              placement="right"
              openDelay={300}
              label="Emergency logout — always visible, bypasses the menu"
            >
              <IconButton
                aria-label="Emergency logout (always visible)"
                icon={<FiLogOut />}
                size="sm"
                borderRadius="full"
                bg="red.400"
                color="white"
                border="2px solid"
                borderColor="rgba(255, 255, 255, 0.3)"
                boxShadow="0 2px 8px rgba(255, 0, 0, 0.3)"
                opacity={0.85}
                onClick={handleLogout}
                _hover={{
                  opacity: 1,
                  bg: 'red.500',
                  transform: 'scale(1.15)',
                  boxShadow: '0 4px 12px rgba(255, 0, 0, 0.5)',
                }}
                transition="all 0.2s"
              />
            </Tooltip>
          </Box>
        </Portal>
      )}

      {/* Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={() => {
          onLoginClose();
          window.location.reload();
        }}
      />
    </>
  );
};
